// ═══════════════════════════════════════════════════════════════
// Keep the box you are typing in above the tablet's keyboard
// ═══════════════════════════════════════════════════════════════
// Drew, 2026-10-16, a photo of Casting's Confirm Scrap on a plant tablet:
// the keyboard came up over the notes box, so nobody could see what they
// typed or fix the two words TALK got wrong. Android Chrome lays its
// keyboard OVER the page by default, so a full-height floor screen and its
// windows never learn they lost the bottom half. Two parts, one rule:
//   1. index.html's viewport asks for `interactive-widget=resizes-content`,
//      so the page itself gets shorter above the keyboard (100dvh shrinks,
//      and a window's max-h / overflow-y-auto lets it scroll), and
//   2. this listener: when a text box takes focus on a touch screen, once
//      the keyboard is up it scrolls that box to the middle of what is
//      still visible, inside whichever window or panel holds it.
// A desk (fine pointer) never runs it. Pure rule in `fieldIsHidden`.
// ═══════════════════════════════════════════════════════════════

/** Room kept clear above the keyboard and below the top edge, in px. */
const MARGIN = 12;

/** Is the box (its rect) cut off by the visible area (0..visibleHeight)? */
export function fieldIsHidden(rect: { top: number; bottom: number }, visibleHeight: number): boolean {
  return rect.bottom > visibleHeight - MARGIN || rect.top < MARGIN;
}

const TYPED = new Set(['text', 'search', 'email', 'tel', 'url', 'number', 'password', '']);

function isTypingBox(el: EventTarget | null): el is HTMLElement {
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return TYPED.has(el.type) && !el.readOnly && !el.disabled;
  return el instanceof HTMLElement && el.isContentEditable;
}

export function keepFieldAboveKeyboard(): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const coarse = window.matchMedia('(pointer: coarse)');
  const timers: number[] = [];
  const reveal = (el: HTMLElement) => {
    if (document.activeElement !== el) return;
    const visible = window.visualViewport?.height ?? window.innerHeight;
    if (fieldIsHidden(el.getBoundingClientRect(), visible)) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };
  const onFocus = (e: FocusEvent) => {
    if (!coarse.matches || !isTypingBox(e.target)) return;
    const el = e.target;
    // The keyboard slides up over ~300ms; look again as it lands and after.
    for (const ms of [120, 350, 700]) timers.push(window.setTimeout(() => reveal(el), ms));
  };
  // The keyboard can also come up (or grow) after focus: follow the resize.
  const onResize = () => {
    const el = document.activeElement;
    if (coarse.matches && isTypingBox(el)) reveal(el);
  };
  document.addEventListener('focusin', onFocus);
  window.visualViewport?.addEventListener('resize', onResize);
  window.addEventListener('resize', onResize);
  return () => {
    document.removeEventListener('focusin', onFocus);
    window.visualViewport?.removeEventListener('resize', onResize);
    window.removeEventListener('resize', onResize);
    timers.forEach(t => window.clearTimeout(t));
  };
}
